<template>
  <Teleport to="body">
    <div v-if="activeStep" class="tutorial-tour" :style="rootStyle" aria-live="polite">
      <template v-if="targetRect">
        <div
          v-for="shade in shadeStyles"
          :key="shade.key"
          class="tutorial-shade"
          :style="shade.style"
        />
        <div class="tutorial-spotlight" :style="spotlightStyle" aria-hidden="true" />
      </template>
      <div v-else class="tutorial-full-shade" :style="fullShadeStyle" />

      <section
        ref="cardRef"
        class="tutorial-card"
        :class="{ 'tutorial-card--centered': !targetRect }"
        :style="visibleCardStyle"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tutorial-title"
        tabindex="-1"
      >
        <p class="tutorial-step-count">Step {{ currentIndex + 1 }} of {{ steps.length }}</p>
        <h2 id="tutorial-title">{{ activeStep.title }}</h2>
        <p class="tutorial-body">{{ activeStep.body }}</p>

        <div class="tutorial-actions">
          <div class="tutorial-navigation">
            <button type="button" :disabled="currentIndex === 0" @click="back">Back</button>
            <button v-if="!isLastStep" type="button" class="primary" @click="next">Next</button>
            <button v-else type="button" class="primary" @click="finish">Finish</button>
          </div>
          <button type="button" class="tutorial-skip" @click="close('skip')">Skip</button>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const VIEWPORT_GUTTER = 8
const CARD_GAP = 16
const LAYERS = Object.freeze({ shade: 2000, spotlight: 2001, card: 2002 })

const props = defineProps({
  steps: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['close'])
const currentIndex = ref(0)
const targetRect = ref(null)
const cardRef = ref(null)
const cardStyle = ref({})
const activeStep = computed(() => props.steps[currentIndex.value])
const isLastStep = computed(() => currentIndex.value === props.steps.length - 1)

let previousFocus = null
let activeTarget = null
let frameId = null
let pendingMeasurement = null
let measurementVersion = 0
let measurementRequest = 0
let listenersInstalled = false
let disposed = true

const rootStyle = Object.freeze({ pointerEvents: 'none', zIndex: LAYERS.shade })
const fullShadeStyle = Object.freeze({ pointerEvents: 'auto', zIndex: LAYERS.shade })
const visibleCardStyle = computed(() => ({
  ...cardStyle.value,
  pointerEvents: 'auto',
  zIndex: LAYERS.card,
}))

const spotlightStyle = computed(() => {
  if (!targetRect.value) return {}
  return {
    ...pxStyle(targetRect.value),
    pointerEvents: 'none',
    zIndex: LAYERS.spotlight,
  }
})

const shadeStyles = computed(() => {
  if (!targetRect.value) return []
  const rect = targetRect.value
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  return [
    {
      key: 'top',
      style: shadeStyle({ top: 0, left: 0, width: viewportWidth, height: rect.top }),
    },
    {
      key: 'left',
      style: shadeStyle({
        top: rect.top,
        left: 0,
        width: rect.left,
        height: rect.height,
      }),
    },
    {
      key: 'right',
      style: shadeStyle({
        top: rect.top,
        left: rect.right,
        width: Math.max(0, viewportWidth - rect.right),
        height: rect.height,
      }),
    },
    {
      key: 'bottom',
      style: shadeStyle({
        top: rect.bottom,
        left: 0,
        width: viewportWidth,
        height: Math.max(0, viewportHeight - rect.bottom),
      }),
    },
  ]
})

function pxStyle(rect) {
  return {
    top: `${Math.max(0, rect.top)}px`,
    left: `${Math.max(0, rect.left)}px`,
    width: `${Math.max(0, rect.width)}px`,
    height: `${Math.max(0, rect.height)}px`,
  }
}

function shadeStyle(rect) {
  return { ...pxStyle(rect), pointerEvents: 'auto', zIndex: LAYERS.shade }
}

function findTarget(selector) {
  if (!selector || typeof selector !== 'string') return null
  try {
    return document.querySelector(selector)
  } catch {
    return null
  }
}

function cancelMeasurement() {
  pendingMeasurement = null
  if (frameId !== null) cancelAnimationFrame(frameId)
  frameId = null
}

function normalizeRect(rect) {
  const top = Math.max(0, rect.top)
  const left = Math.max(0, rect.left)
  const right = Math.min(window.innerWidth, rect.right)
  const bottom = Math.min(window.innerHeight, rect.bottom)
  return {
    top,
    right,
    bottom,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  }
}

function measurementIsCurrent(measurement) {
  return (
    !disposed &&
    activeStep.value &&
    measurement.version === measurementVersion &&
    measurement.target === activeTarget &&
    measurement.target.isConnected
  )
}

function scheduleMeasurement(target = activeTarget) {
  if (!target || !activeStep.value) return
  const request = ++measurementRequest
  pendingMeasurement = {
    request,
    side: activeStep.value.side,
    target,
    version: measurementVersion,
  }
  if (frameId !== null) return

  frameId = requestAnimationFrame(() => {
    frameId = null
    const measurement = pendingMeasurement
    pendingMeasurement = null
    if (!measurement || !measurementIsCurrent(measurement)) {
      if (measurement?.target === activeTarget && !measurement.target.isConnected) {
        activeTarget = null
        targetRect.value = null
        cardStyle.value = {}
      }
      return
    }

    const rect = normalizeRect(measurement.target.getBoundingClientRect())
    targetRect.value = rect
    void nextTick().then(() => {
      if (!measurementIsCurrent(measurement) || measurement.request !== measurementRequest) return
      cardStyle.value = calculateCardStyle(rect, measurement.side)
    })
  })
}

function calculateCardStyle(rect, preferredSide = 'bottom') {
  const measured = cardRef.value?.getBoundingClientRect()
  const width = measured?.width || 360
  const height = measured?.height || 240
  const centeredLeft = rect.left + rect.width / 2 - width / 2
  const centeredTop = rect.top + rect.height / 2 - height / 2
  const candidates = {
    top: { left: centeredLeft, top: rect.top - height - CARD_GAP },
    right: { left: rect.right + CARD_GAP, top: centeredTop },
    bottom: { left: centeredLeft, top: rect.bottom + CARD_GAP },
    left: { left: rect.left - width - CARD_GAP, top: centeredTop },
  }
  const order = [preferredSide, 'bottom', 'top'].filter(
    (side, index, sides) => candidates[side] && sides.indexOf(side) === index,
  )
  const position = order
    .map((side) => candidates[side])
    .find((candidate) => fitsViewport(candidate, width, height)) || {
    left: centeredLeft,
    top: centeredTop,
  }

  return {
    left: `${clamp(position.left, VIEWPORT_GUTTER, window.innerWidth - width - VIEWPORT_GUTTER)}px`,
    top: `${clamp(position.top, VIEWPORT_GUTTER, window.innerHeight - height - VIEWPORT_GUTTER)}px`,
  }
}

function fitsViewport(position, width, height) {
  return (
    position.left >= VIEWPORT_GUTTER &&
    position.top >= VIEWPORT_GUTTER &&
    position.left + width <= window.innerWidth - VIEWPORT_GUTTER &&
    position.top + height <= window.innerHeight - VIEWPORT_GUTTER
  )
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum))
}

function enabledButtons() {
  return [...(cardRef.value?.querySelectorAll('button:not(:disabled)') || [])]
}

function trapFocus(event) {
  const buttons = enabledButtons()
  if (!buttons.length) {
    event.preventDefault()
    cardRef.value?.focus()
    return
  }

  const first = buttons[0]
  const last = buttons[buttons.length - 1]
  if (!cardRef.value?.contains(document.activeElement)) {
    event.preventDefault()
    if (event.shiftKey) last.focus()
    else first.focus()
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    close('escape')
  } else if (event.key === 'Tab') {
    trapFocus(event)
  }
}

function close(reason) {
  emit('close', reason)
}

function back() {
  if (currentIndex.value > 0) currentIndex.value -= 1
}

function next() {
  if (!isLastStep.value) currentIndex.value += 1
}

function finish() {
  close('finish')
}

function refreshPosition() {
  scheduleMeasurement()
}

function installListeners() {
  if (listenersInstalled) return
  previousFocus = document.activeElement
  window.addEventListener('keydown', onKeydown)
  window.addEventListener('resize', refreshPosition)
  window.addEventListener('scroll', refreshPosition, true)
  listenersInstalled = true
}

function removeListeners({ restoreFocus = true } = {}) {
  if (!listenersInstalled) return
  window.removeEventListener('keydown', onKeydown)
  window.removeEventListener('resize', refreshPosition)
  window.removeEventListener('scroll', refreshPosition, true)
  listenersInstalled = false
  if (restoreFocus && previousFocus?.isConnected && typeof previousFocus.focus === 'function') {
    previousFocus.focus()
  }
  previousFocus = null
}

function deactivateStep() {
  measurementVersion += 1
  cancelMeasurement()
  activeTarget = null
  targetRect.value = null
  cardStyle.value = {}
}

function activateStep() {
  if (disposed) return
  deactivateStep()
  if (!activeStep.value) {
    removeListeners()
    return
  }

  installListeners()
  activeTarget = findTarget(activeStep.value.target)
  if (activeTarget) {
    activeTarget.scrollIntoView?.({ block: 'center', inline: 'nearest' })
    scheduleMeasurement(activeTarget)
  }
  const version = measurementVersion
  void nextTick().then(() => {
    if (!disposed && version === measurementVersion) cardRef.value?.focus()
  })
}

function synchronizeSteps() {
  const maximumIndex = Math.max(0, props.steps.length - 1)
  const normalizedIndex = Math.min(currentIndex.value, maximumIndex)
  if (normalizedIndex !== currentIndex.value) {
    currentIndex.value = normalizedIndex
    return
  }
  activateStep()
}

watch(currentIndex, activateStep)
watch(() => props.steps, synchronizeSteps, { deep: true })

onMounted(() => {
  disposed = false
  synchronizeSteps()
})

onUnmounted(() => {
  disposed = true
  deactivateStep()
  removeListeners()
})
</script>

<style scoped>
.tutorial-tour {
  position: fixed;
  inset: 0;
  font-family: var(--font-sans);
}

.tutorial-shade,
.tutorial-full-shade {
  position: fixed;
  background: rgb(8 12 18 / 72%);
}

.tutorial-full-shade {
  inset: 0;
}

.tutorial-spotlight {
  position: fixed;
  border: 3px solid var(--color-accent, #4da3ff);
  border-radius: var(--radius-md, 6px);
  box-sizing: border-box;
  box-shadow: 0 0 0 4px rgb(77 163 255 / 24%);
}

.tutorial-card {
  position: fixed;
  box-sizing: border-box;
  width: min(360px, calc(100vw - 16px));
  max-height: calc(100vh - 16px);
  overflow-y: auto;
  padding: var(--space-5, 20px);
  border: 1px solid var(--color-border, #455160);
  border-radius: var(--radius-lg, 10px);
  background: var(--color-bg-raised, #202733);
  color: var(--color-text, #f3f6fa);
  box-shadow: var(--shadow-lg, 0 18px 48px rgb(0 0 0 / 45%));
}

.tutorial-card--centered {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.tutorial-card:focus {
  outline: 2px solid var(--color-accent, #4da3ff);
  outline-offset: 3px;
}

.tutorial-step-count {
  margin: 0 0 var(--space-2, 8px);
  color: var(--color-text-muted, #aeb8c5);
  font-size: var(--fs-sm, 0.875rem);
  font-weight: 600;
}

h2 {
  margin: 0 0 var(--space-3, 12px);
  font-size: 1.25rem;
}

.tutorial-body {
  margin: 0;
  color: var(--color-text-muted, #c5ccd5);
  line-height: 1.5;
}

.tutorial-actions,
.tutorial-navigation {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
}

.tutorial-actions {
  justify-content: space-between;
  margin-top: var(--space-5, 20px);
}

button {
  padding: var(--space-2, 8px) var(--space-3, 12px);
  border: 1px solid var(--color-border, #455160);
  border-radius: var(--radius-sm, 4px);
  background: var(--color-bg-subtle, #293342);
  color: var(--color-text, #f3f6fa);
  font: inherit;
  cursor: pointer;
}

button:hover:not(:disabled) {
  background: var(--color-bg, #151b24);
}

button:focus-visible {
  outline: 2px solid var(--color-accent, #4da3ff);
  outline-offset: 2px;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

button.primary {
  border-color: var(--color-accent, #4da3ff);
  background: var(--color-accent, #247cca);
  color: #fff;
}

.tutorial-skip {
  border-color: transparent;
  background: transparent;
  color: var(--color-text-muted, #c5ccd5);
}
</style>
